import { LoadingArea } from '@consigli/facade';
import { useDocumentPackageResults, usePackage, usePackageId, useProjectId } from '@consigli/hooks';
import { convertFileToDocumentWithFileType } from '@consigli/types';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CardBasedFileDisplay, RESULT_SECTIONS } from '@/components/card-based-file-display';
import { DismissibleDisclaimer } from '@/components/dismissible-disclaimer';
import { WaitingForAnalysis } from '@/components/waiting-for-analysis';
import { Breadcrumb } from '@/features/breadcrumb/breadcrumb';
import { WorkspaceProvider } from '@/layouts/workspace-layout/use-workspace-context';
import { WorkspaceLayout } from '@/layouts/workspace-layout/workspace-layout';
import { useDemoMode } from '@/util/demo-mode';

export const LuminaireScheduleResultsPage: FC = () => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const packageId = usePackageId();
  const { adminFeaturesEnabled } = useDemoMode();

  const { pkg } = usePackage(projectId, packageId);
  const isPublished = pkg ? pkg.published : false;

  const { results, isLoading, isFetching, isError } = useDocumentPackageResults({
    projectId,
    documentPackageId: packageId,
    page: 'all',
  });

  const documents = useMemo(() => {
    return results.map((result) => convertFileToDocumentWithFileType(result.file, result.type));
  }, [results]);

  const waitingForAnalysis = useMemo(() => {
    if (adminFeaturesEnabled) {
      return false;
    }
    return !isPublished;
  }, [isPublished, adminFeaturesEnabled]);

  return (
    <WorkspaceProvider>
      <WorkspaceLayout hideTabs={!packageId || waitingForAnalysis}>
        <Breadcrumb label={t('luminaire-schedule-tabs.results')} />
        {waitingForAnalysis ? (
          <WaitingForAnalysis />
        ) : isLoading ? (
          <LoadingArea title={t('document-list.loading')} />
        ) : isError || documents.length === 0 ? (
          <div className="flex justify-center items-center w-full h-full text-[20px]">
            {t('document-list.no-results')}
          </div>
        ) : (
          <>
            <DismissibleDisclaimer>{t('optimization-tabs.ai-disclaimer')}</DismissibleDisclaimer>
            <div className="flex">
              <CardBasedFileDisplay
                files={documents}
                isFetching={isFetching}
                isPublished={isPublished}
                sections={RESULT_SECTIONS}
              />
            </div>
          </>
        )}
      </WorkspaceLayout>
    </WorkspaceProvider>
  );
};
